/**
 * Session replay: watch a run back on a clock.
 *
 * The playhead moves in real time (times the speed) across a timeline whose
 * x-axis is real elapsed time, so markers sit where the events happened and
 * the run plays back at the pace it was lived at. It never skips ahead: an
 * earlier build jumped the cursor over any quiet stretch, which made the bar
 * teleport between events and the clock lie about how long a step took. A
 * quiet minute is information, and the speed control is how you get through
 * it.
 *
 * Selection is derived from the cursor — clicking an event moves the clock to
 * it — so the timeline, the rail and the detail can never disagree.
 *
 * This file resolves loading, error and empty states; the view itself is
 * exported so it can be tested with synthetic events.
 */
import { ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReplayEvent, ReplayEventType, ReplayResponse } from '../api/replay';
import { ReplayDetailsPanel } from '../components/replay/ReplayDetailsPanel';
import { ReplayEventList } from '../components/replay/ReplayEventList';
import { makeDefaultFilters, ReplayFilters, type Filters } from '../components/replay/ReplayFilters';
import {
  ReplayPlaybackControls,
  type PlaybackSpeed,
} from '../components/replay/ReplayPlaybackControls';
import { ReplayTimeline } from '../components/replay/ReplayTimeline';
import {
  EmptyState,
  ErrorState,
  formatDateTime,
  Lightbox,
  Page,
  PageHeader,
  Panel,
  SkeletonRows,
  StatusBadge,
} from '../components/patterns';
import { href } from '../router';
import { useReplay } from '../hooks/useReplay';

export function SessionReplayPage({ sessionId }: { sessionId: string }) {
  const query = useReplay(sessionId);

  if (query.isPending) {
    return (
      <Page>
        <Panel flush>
          <SkeletonRows rows={6} />
        </Panel>
      </Page>
    );
  }
  if (query.error) {
    return (
      <Page>
        <Panel>
          <ErrorState error={query.error} retry={() => void query.refetch()} />
        </Panel>
      </Page>
    );
  }
  if (!query.data) {
    return (
      <Page>
        <Panel>
          <EmptyState title="No session data" />
        </Panel>
      </Page>
    );
  }
  return <SessionReplayView data={query.data} />;
}

export function SessionReplayView({ data }: { data: ReplayResponse }) {
  const { session, events } = data;
  const [filters, setFilters] = useState<Filters>(() => makeDefaultFilters());
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<PlaybackSpeed>(1);
  const [cursor, setCursor] = useState(0);
  const [photo, setPhoto] = useState<string | undefined>();

  const visible = useMemo(() => events.filter((e) => filters[e.type]), [events, filters]);

  // Everything on this screen is measured from the first visible event, so
  // the ruler, the rail and the clock agree on where zero is.
  const baseIso = visible[0]?.timestamp ?? session.startedAt;
  const base = Date.parse(baseIso);
  const offsets = useMemo(
    () => visible.map((e) => Math.max(0, Date.parse(e.timestamp) - base)),
    [visible, base],
  );
  const span = offsets.length > 0 ? offsets[offsets.length - 1]! : 0;

  const counts = useMemo(() => {
    const out: Partial<Record<ReplayEventType, number>> = {};
    for (const e of events) out[e.type] = (out[e.type] ?? 0) + 1;
    return out;
  }, [events]);

  // The cursor is the source of truth; the frame loop reads it through a ref
  // so it never restarts on every tick.
  const cursorRef = useRef(cursor);
  const moveTo = useCallback((ms: number) => {
    cursorRef.current = ms;
    setCursor(ms);
  }, []);

  useEffect(() => {
    if (!playing) return;
    if (offsets.length < 2) {
      setPlaying(false);
      return;
    }
    let raf = 0;
    let last = performance.now();
    const step = (now: number) => {
      // Cap the delta so a backgrounded tab does not jump the whole run.
      const dt = Math.min(250, now - last);
      last = now;
      const next = cursorRef.current + dt * speed;
      if (next >= span) {
        moveTo(span);
        setPlaying(false);
        return;
      }
      moveTo(next);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [playing, speed, span, offsets, moveTo]);

  // Selection follows the clock: the most recent event at or before it.
  const selectedIndex = useMemo(() => {
    let idx = -1;
    for (let i = 0; i < offsets.length; i += 1) {
      if (offsets[i]! <= cursor + 1) idx = i;
      else break;
    }
    return idx === -1 && visible.length > 0 ? 0 : idx;
  }, [offsets, cursor, visible.length]);
  const selectedEvent: ReplayEvent | null = visible[selectedIndex] ?? null;
  const selectedEventId = selectedEvent?.id ?? null;

  const selectEvent = useCallback(
    (id: string) => {
      const i = visible.findIndex((e) => e.id === id);
      if (i === -1) return;
      setPlaying(false);
      moveTo(offsets[i] ?? 0);
    },
    [visible, offsets, moveTo],
  );

  const stepEvent = useCallback(
    (direction: -1 | 1) => {
      const next = selectedIndex + direction;
      if (next < 0 || next >= visible.length) return;
      setPlaying(false);
      moveTo(offsets[next] ?? 0);
    },
    [selectedIndex, visible.length, offsets, moveTo],
  );

  const togglePlay = useCallback(() => {
    setPlaying((p) => {
      if (p) return false;
      // Play from the top when the run has already finished.
      if (cursorRef.current >= span) moveTo(0);
      return true;
    });
  }, [span, moveTo]);

  // Space plays, arrows step. Ignored while typing.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const el = e.target as HTMLElement | null;
      if (el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
      if (e.key === ' ') {
        e.preventDefault();
        togglePlay();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        stepEvent(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        stepEvent(1);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePlay, stepEvent]);

  return (
    <Page>
      <div>
        <a
          href={href({ name: 'session', id: session.id })}
          className="-ml-2 inline-flex items-center gap-1.5 rounded-control px-2 py-1 text-meta font-medium text-ink-dim transition-colors hover:bg-muted hover:text-ink"
        >
          <ArrowLeft className="size-4" /> Session
        </a>
      </div>

      <PageHeader
        eyebrow="Replay"
        title={
          <span className="inline-flex flex-wrap items-center gap-3">
            {session.workerName}
            <StatusBadge
              tone={
                session.status === 'completed'
                  ? 'verified'
                  : session.status === 'aborted'
                    ? 'retry'
                    : 'field'
              }
              live={session.status === 'active'}
            >
              {session.status}
            </StatusBadge>
            {session.finalOutcome ? (
              <StatusBadge
                tone={
                  session.finalOutcome === 'pass'
                    ? 'verified'
                    : session.finalOutcome === 'fail'
                      ? 'error'
                      : 'neutral'
                }
              >
                {session.finalOutcome}
              </StatusBadge>
            ) : null}
          </span>
        }
        lede={
          <>
            {session.procedureTitle} · started {formatDateTime(session.startedAt)}
            {session.endedAt ? ` · ended ${formatDateTime(session.endedAt)}` : ''} · {events.length}{' '}
            events
            {visible.length !== events.length ? `, ${visible.length} shown` : ''}
          </>
        }
      />

      <Panel bodyClassName="flex flex-col gap-4">
        <ReplayTimeline
          events={visible}
          selectedEventId={selectedEventId}
          onSelectEvent={selectEvent}
          cursorMs={cursor}
          onScrub={(ms) => {
            setPlaying(false);
            moveTo(ms);
          }}
        />
        <div className="flex flex-col gap-3 border-t border-line pt-3 xl:flex-row xl:items-center xl:justify-between">
          <ReplayPlaybackControls
            playing={playing}
            speed={speed}
            onTogglePlay={togglePlay}
            onChangeSpeed={setSpeed}
            onStep={stepEvent}
            cursorMs={cursor}
            durationMs={span}
            disabled={visible.length < 2}
          />
          <ReplayFilters filters={filters} onChange={setFilters} counts={counts} />
        </div>
      </Panel>

      {/* The rail is fixed-width and the detail takes the rest: the detail
          carries the photo, so it is the half that wants the room. */}
      <div className="grid min-h-0 gap-5 lg:grid-cols-[20rem_minmax(0,1fr)] lg:items-start">
        <Panel
          flush
          title="Events"
          description={`${visible.length} shown`}
          bodyClassName="max-h-[32rem] overflow-y-auto border-t border-line lg:max-h-[calc(100vh-16rem)]"
        >
          <ReplayEventList
            events={visible}
            selectedEventId={selectedEventId}
            onSelectEvent={selectEvent}
            sessionStartIso={baseIso}
            cursorMs={cursor}
          />
        </Panel>

        <Panel flush className="lg:sticky lg:top-20" bodyClassName="min-h-[20rem]">
          <ReplayDetailsPanel selectedEvent={selectedEvent} onPhoto={setPhoto} />
        </Panel>
      </div>

      <Lightbox src={photo} onClose={() => setPhoto(undefined)} />
    </Page>
  );
}
